// ランチャー経由だけ許可（サーバーごと）。
import { useState } from 'react';
import { layoutFamilyOf, type LauncherGateSettings } from '@mcpanel/core';
import { rawRequest } from '../api/client';
import type { ServerView } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { Spinner } from '../components/feedback';
import { ErrorAlert } from '../components/ui';
import { usePoll } from '../hooks/usePoll';
import { LauncherGateExemptList } from './LauncherGateExempt';

const path = (serverId: string) => `/servers/${encodeURIComponent(serverId)}/launcher-gate`;

export function LauncherGateCard({ server }: { server: ServerView }) {
  const { canOperate, notify } = useApp();
  const gate = usePoll(() => rawRequest<LauncherGateSettings>('GET', path(server.id)), 60000, [server.id]);
  const [busy, setBusy] = useState(false);
  const save = async (settings: LauncherGateSettings) => {
    setBusy(true);
    try {
      await rawRequest<LauncherGateSettings>('PUT', path(server.id), settings);
      await gate.reload();
      notify(
        settings.enabled ? 'ランチャー経由だけ入れるようにしました' : 'どのクライアントからも入れるようにしました',
      );
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };
  const value = gate.data;
  const paper = layoutFamilyOf(server.software.family) === 'bukkit';
  return (
    <section className="card card-body stack" style={{ gap: 8 }}>
      <h2 data-search-id="launcher-gate">入れるクライアントの制限</h2>
      <ErrorAlert error={gate.error} />
      {value && (
        <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0, gap: 6 }} disabled={!canOperate || busy}>
          <label className="check">
            <input
              type="checkbox"
              checked={value.enabled}
              onChange={(event) => void save({ ...value, enabled: event.target.checked })}
            />
            ランチャー（Shouchan Launcher）から起動した人だけ入れる
            {busy && <Spinner />}
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={value.allowBedrock}
              onChange={(event) => void save({ ...value, allowBedrock: event.target.checked })}
            />
            統合版の人は通す（統合版はランチャーを使えません）
          </label>
        </fieldset>
      )}
      <LauncherGateExemptList />
      <p className="small muted" style={{ margin: 0 }}>
        ランチャーがゲームを起動するときに、Mojang の本人確認でパネルに知らせた人だけ入れます（確認は 10
        分ごとに更新されます）。OP の人と、下の「ランチャーなしで入れる人」はいつでも入れます。
        {paper
          ? ' このサーバーでは、ログインの前に断ります。'
          : ' このサーバー（Paper 系以外）では、入った直後にキックします。一瞬だけ参加のお知らせが出ます。'}
      </p>
      <p className="alert warn small" style={{ margin: 0 }}>
        この仕組みに対応したランチャーが出るまでは、有効にすると OP 以外の Java 版の人は入れなくなります。
        ランチャーは手元で動くので、作り込めば真似できます。荒らしの手間を増やすためのものと考えてください。
      </p>
    </section>
  );
}
